import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { RegulationParty } from './jobstisio.service';

export interface PartyRun {
  runId: string;
  createdAt: string;
  count: number;
  model: string;
  vision: boolean;
  verified: boolean;
}

interface LatestResponse {
  parties: RegulationParty[];
  runs: PartyRun[];
  runId?: string;
}

/**
 * Shared state for regulation party extraction surfaces (public tab, admin
 * section). Triggering enqueues an async worker task; the latest-run query
 * polls while the inspector reports a live task, so a run in flight stays
 * visible even across reloads. History comes from the rows themselves —
 * every run_id with its party count — selectable to inspect past runs.
 */
export function useRegulationParties(regulationId: string) {
  const queryClient = useQueryClient();
  const [watching, setWatching] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  // When the watched trigger fired. Stopping waits for a run NEWER than
  // this — with pre-existing results the old stop rule (any rows present)
  // killed polling seconds after trigger, hiding the whole run.
  const triggerAtRef = useRef<number>(0);
  const latestKey = useMemo(() => ['regulation-parties', regulationId, selectedRunId], [regulationId, selectedRunId]);
  const activeKey = useMemo(() => ['regulation-parties-active', regulationId], [regulationId]);

  const active = useQuery<{ running: boolean }>({
    queryKey: activeKey,
    queryFn: async () => {
      const res = await fetch(`/api/regulation/${regulationId}/parties/active`, {
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('Failed to check extraction status');
      return res.json();
    },
    refetchInterval: watching ? 4000 : false,
  });

  const latest = useQuery<LatestResponse>({
    queryKey: latestKey,
    queryFn: async () => {
      const url =
        `/api/regulation/${regulationId}/parties` +
        (selectedRunId ? `?runId=${encodeURIComponent(selectedRunId)}` : '');
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to load parties');
      return res.json();
    },
    refetchInterval: watching ? 4000 : false,
  });

  const trigger = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/regulation/${regulationId}/parties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to start extraction');
      }
      return res.json();
    },
    onSuccess: () => {
      triggerAtRef.current = Date.now();
      setSelectedRunId(null);
      setWatching(true);
    },
  });

  const parties = latest.data?.parties ?? [];
  const runs = latest.data?.runs ?? [];
  const running = active.data?.running ?? false;

  // A live task found on mount (e.g. after reload) starts polling even
  // though no trigger happened in this session — otherwise the badge
  // shows once and completion never arrives.
  useEffect(() => {
    if (active.data?.running) setWatching(true);
  }, [active.data?.running]);

  // Stop watching once the worker goes quiet AND the newest run postdates
  // the trigger. Older runs must not satisfy this — otherwise polling dies
  // while the fresh run is still queued.
  const newestCreatedAt = runs[0]?.createdAt;
  useEffect(() => {
    if (!watching || running || parties.length === 0) return;
    if (!newestCreatedAt || new Date(newestCreatedAt).getTime() <= triggerAtRef.current) return;
    const t = setTimeout(() => {
      setWatching(false);
      setSelectedRunId(null);
      queryClient.invalidateQueries({ queryKey: latestKey });
    }, 3000);
    return () => clearTimeout(t);
  }, [watching, running, parties.length, newestCreatedAt, queryClient, latestKey]);

  return {
    parties,
    runs,
    running,
    watching,
    selectedRunId,
    setSelectedRunId,
    setWatching,
    latest,
    trigger,
  };
}
